<template>
  <div class="product-carousel">
    <h2 class="product-carousel__title">{{ title }}</h2>

    <!--
      Il prodotto si scorre con le frecce del block condiviso o coi dots, che
      sono markup di pagina (come in @components/blocks/hero.vue e nelle
      homepage di booking/bookingv3/liveeps).
    -->
    <swiper
      ref_id="livegift:products"
      :class="{
        'product-carousel__track--at-start': current_product === 0,
        'product-carousel__track--at-end': current_product === products.length - 1,
      }"
      :swiper_params="carousel_params"
      @swiper-initialized="on_carousel_ready"
    >
      <swiper-slide
        v-for="product in products"
        :key="product.id"
        class="product-carousel__slide"
      >
        <!-- card di sola vetrina: non è cliccabile, la navigazione è fuori -->
        <gift-card
          :category="product.category"
          :logo_src="product.logo"
          :validity="validity"
        />
      </swiper-slide>
    </swiper>

    <!-- con una sola card in catalogo il dot non naviga da nessuna parte -->
    <div v-if="products.length > 1" class="product-carousel__pagination">
      <button
        v-for="(product, product_index) in products"
        :key="`dot:${product.id}`"
        type="button"
        class="product-carousel__dot"
        :class="{
          'product-carousel__dot--active': product_index === current_product,
        }"
        :aria-label="`Vai a ${product.category}`"
        @click="go_to_product(product_index)"
      ></button>
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import Swiper from '@components/blocks/swiper.vue';
  import { VALIDITY_LABEL } from '@livegift/resources';

  export default {
    name: 'product-carousel',

    components: {
      'swiper': Swiper,
    },

    data() {
      return {
        // elemento <swiper-container>, ricevuto da @swiper-initialized
        carousel: {},
        carousel_params: {
          // il block condiviso applica `classes` alla sua root: passandola qui
          // si evita la classe "undefined" che genererebbe il template
          classes: 'product-carousel__track',
          slidesPerView: 1,
          loop: false,
          autoHeight: true,
          // lo stacco fra le card lo gestisce Swiper, che lo conta nel calcolo
          // delle larghezze: un margin in SCSS sfaserebbe la traslazione
          spaceBetween: 15,
          // le frecce escono dai lati, così restano nel padding del pannello
          // senza coprire la card (come invadobay e branded-fare-upsell)
          button_offset: 25,
          controls_size: 15,
          // con una sola card in catalogo Swiper va in lock (tutte le slide ci
          // stanno) e il block condiviso toglie le frecce da sé: non c'è niente
          // da scorrere. Torna a mostrarle se un domani le card saranno di più
          hide_controls_on_lock: true,
          on: {
            slideChange: (swiper) => {
              this.select_product(swiper.realIndex);
            },
          },
        },
      };
    },

    computed: {
      ...mapState('livegift', ['products', 'selected_product']),

      validity: () => VALIDITY_LABEL,

      // il numero dei partner non è più un dato fisso di copy: sono le card che
      // il catalogo vende davvero
      title() {
        if (this.products.length === 1) return 'Vadobay: un’agenzia, un partner esclusivo';
        return `Vadobay: un’agenzia, ${this.products.length} partner esclusivi`;
      },

      // indice del prodotto a vista: deriva dallo store, così la colonna destra
      // e questo carousel restano allineati senza passarsi prop
      current_product() {
        return this.products.findIndex((product) => product.id === this.selected_product);
      },
    },

    methods: {
      on_carousel_ready(instance) {
        this.carousel = instance;
      },

      select_product(product_index) {
        const product = this.products[product_index];
        if (product) this.$store.commit('livegift/selected_product', product.id);
      },

      // click su un dot: porta il carousel sul prodotto scelto. Lo store lo
      // aggiorna slideChange, così se lo scorrimento non avviene lo stato non
      // si muove e dot, slide e colonna destra non vanno fuori sincrono
      go_to_product(product_index) {
        if (this.carousel && this.carousel.swiper) {
          this.carousel.swiper.slideTo(product_index);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>

  .product-carousel {

    &__title {
      margin: 0 0 $grid-gutter-20;
      font-size: $font-size-base * 1.5;
      font-weight: $vb-font-weight-semibold;
      line-height: 32px;
      color: $gray-900;
    }

    // il block condiviso non prevede lo stato disabilitato e il carousel non fa
    // loop: ai capi la freccia che non porta da nessuna parte si spegne da qui
    &__track--at-start ::v-deep .swiper__button__prev,
    &__track--at-end ::v-deep .swiper__button__next {
      opacity: .4;
      pointer-events: none;
    }

    ::v-deep .swiper__button {
      @include media-breakpoint-down(md) {
        display: none;
      }
    }

    &__pagination {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: $grid-gutter-width / 2;
    }

    // dot: cerchio da 8px che si allunga a pillola quando è quello attivo
    &__dot {
      width: 8px;
      height: 8px;
      padding: 0;
      background-color: $gray-400;
      border: 0;
      border-radius: 100px;
      transition: width .15s ease, background-color .15s ease;

      & + & {
        margin-left: 8px;
      }

      &--active {
        width: 20px;
        background-color: $info;
      }
    }
  }
</style>